<script setup lang="ts">
import type { ProductReviewFragment } from '#graphql-operations'
import { upperFirst } from 'scule'

const props = defineProps<{
  reviews: ProductReviewFragment[]
}>()

const { t, d } = useI18n()

const showReviewDetail = ref(false)

const currentFilteredReviewIndex = ref(0)
const currentAssetIndex = ref(0)

const productReviewStore = useProductReviewsStore()

const { reviews } = toRefs(props)

const reviewsWithAssets = computed(() => reviews.value.filter(review => review?.assets?.length > 0))
const review = computed(() => {
  const idx = currentFilteredReviewIndex.value
  return idx >= 0 && idx < reviewsWithAssets.value.length ? reviewsWithAssets.value[idx] : null
})

const purchasedProductOptions = computed(() => review.value?.productVariant?.options.map(option => upperFirst(option.name)).join(' / '))

function onNextClick() {
  // If there are more assets in current review
  if (review.value?.assets?.length && currentAssetIndex.value < review.value.assets.length - 1) {
    // Move to next asset in current review
    currentAssetIndex.value += 1
    return
  }

  // Check if there's a next review with assets
  if (currentFilteredReviewIndex.value < reviewsWithAssets.value.length - 1) {
    // Move to the first asset of the next review
    currentFilteredReviewIndex.value += 1
    currentAssetIndex.value = 0
  }
}

function onPrevClick() {
  // If there are previous assets in current review
  if (review.value?.assets?.length && currentAssetIndex.value > 0) {
    // Move to previous asset in current review
    currentAssetIndex.value -= 1
    return
  }

  // Check if there's a previous review with assets
  if (currentFilteredReviewIndex.value > 0) {
    // Move to the last asset of the previous review
    currentFilteredReviewIndex.value -= 1
    const prevReview = reviewsWithAssets.value[currentFilteredReviewIndex.value]
    if (prevReview?.assets?.length) {
      currentAssetIndex.value = prevReview.assets.length - 1
    }
    else {
      currentAssetIndex.value = 0
    }
  }
}

function onOpenReview(value: ProductReviewFragment, assetIndex: number) {
  // Only open if the review has assets
  if (!value.assets || value.assets.length === 0)
    return

  const reviewIndex = reviewsWithAssets.value.findIndex(r => r.id === value.id)
  if (reviewIndex >= 0) {
    currentFilteredReviewIndex.value = reviewIndex
    currentAssetIndex.value = assetIndex >= 0 && assetIndex < value.assets.length ? assetIndex : 0
    showReviewDetail.value = true
  }
}

const showPrevButton = computed(() => {
  if (!review.value)
    return false

  const hasCurrentAssets = currentAssetIndex.value > 0
  return hasCurrentAssets || currentFilteredReviewIndex.value > 0
})

const showNextButton = computed(() => {
  if (!review.value)
    return false

  const hasMoreCurrentAssets = review.value.assets?.length && currentAssetIndex.value < review.value.assets.length - 1
  return hasMoreCurrentAssets || currentFilteredReviewIndex.value < reviewsWithAssets.value.length - 1
})

async function handleUpvote(review: ProductReviewFragment) {
  if (productReviewStore.hasVoted(review.id)) {
    // await productReviewStore.voteOnReview(review.id, false)
  }
  else {
    await productReviewStore.voteOnReview(review, true)
    productReviewStore.markAsVoted(review.id)
  }
}

const totalItems = computed(() => {
  return reviewsWithAssets.value.reduce((acc, review) => acc + (review.assets?.length || 0), 0)
})

const currentItemIndex = computed(() => {
  if (currentFilteredReviewIndex.value < 0)
    return 0

  let index = 0
  for (let i = 0; i < currentFilteredReviewIndex.value; i++) {
    index += reviewsWithAssets.value[i]?.assets?.length || 0
  }

  return index + Math.min(currentAssetIndex.value, review.value?.assets?.length ? review.value.assets.length - 1 : 0)
})
</script>

<template>
  <div>
    <div class="space-y-6">
      <ProductReviewListItem
        v-for="(item, index) in reviews"
        :key="index"
        :review="item"
        @open="onOpenReview"
        @upvote="handleUpvote"
      />
    </div>

    <UiDialog v-if="review" v-model:open="showReviewDetail">
      <UiDialogContent class="h-screen max-w-screen border-0 p-0 md:h-auto md:max-w-5xl md:border-1 md:p-6">
        <!-- Explicit close button for mobile -->
        <UiButton
          variant="ghost"
          size="icon"
          class="absolute right-2 top-2 z-50 size-8 rounded-full bg-white/80 shadow-sm md:hidden"
          @click="showReviewDetail = false"
        >
          <Icon name="i-heroicons:x-mark" class="size-5" />
          <span class="sr-only">{{ t('general.close') }}</span>
        </UiButton>
        <UiDialogHeader class="sr-only">
          <UiDialogTitle>
            {{ t('product.review_detail') }}
          </UiDialogTitle>
          <UiDialogDescription>
            {{ t('product.review_detail_description') }}
          </UiDialogDescription>
        </UiDialogHeader>

        <div class="relative grid grid-cols-1 h-full gap-6 overflow-hidden bg-background md:grid-cols-2 md:h-[70vh]">
          <!-- Counter for mobile -->
          <div class="absolute inset-x-0 top-4 z-10 text-center md:hidden">
            <span class="rounded-full bg-black/70 px-3 py-1 text-sm text-white font-medium">
              {{ currentItemIndex + 1 }} / {{ totalItems }}
            </span>
          </div>

          <!-- Asset viewer -->
          <div class="relative h-[50vh] overflow-hidden bg-black/95 md:h-full md:rounded-lg md:bg-muted">
            <div class="h-full w-full flex items-center justify-center">
              <div v-if="review.assets?.length && review.assets[currentAssetIndex]" class="relative h-full w-full">
                <video
                  v-if="isVideo(review.assets[currentAssetIndex]!.type)"
                  class="h-full w-full object-cover md:object-contain"
                  :poster="review.assets[currentAssetIndex]!.preview"
                  autoplay controls
                >
                  <source
                    :src="review.assets[currentAssetIndex]!.source"
                    :type="review.assets[currentAssetIndex]!.mimeType"
                  >
                </video>
                <NuxtImg
                  v-else
                  :src="review.assets[currentAssetIndex]!.preview"
                  class="pointer-events-none h-full w-full select-none object-cover md:object-contain"
                  placeholder
                />
              </div>
            </div>

            <!-- Navigation buttons -->
            <div
              v-if="showPrevButton"
              class="absolute left-0 top-0 z-40 h-full flex items-center justify-start bg-transparent px-4"
            >
              <UiButton
                variant="outline"
                size="icon"
                class="size-10 rounded-full bg-white/80 shadow-md hover:bg-white"
                @click.stop="onPrevClick"
              >
                <Icon name="i-heroicons:chevron-left" class="size-5" />
              </UiButton>
            </div>

            <div v-if="showNextButton" class="absolute right-0 top-0 z-40 h-full flex items-center justify-end bg-transparent px-4">
              <UiButton
                variant="outline"
                size="icon"
                class="size-10 rounded-full bg-white/80 shadow-md hover:bg-white"
                @click.stop="onNextClick"
              >
                <Icon name="i-heroicons:chevron-right" class="size-5" />
              </UiButton>
            </div>
          </div>

          <!-- Review details -->
          <div class="flex flex-col overflow-y-auto rounded-b-lg bg-white p-6 md:rounded-lg dark:bg-gray-900 md:dark:bg-gray-900">
            <div class="flex flex-col items-start justify-start">
              <!-- Author info -->
              <div class="mb-4 w-full flex items-center gap-3">
                <UiAvatar class="h-10 w-10 border shadow-sm">
                  <UiAvatarFallback class="bg-primary/5">
                    <Icon name="i-heroicons:user-20-solid" class="size-5 text-primary/70" />
                  </UiAvatarFallback>
                </UiAvatar>

                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="text-foreground font-semibold">
                      {{ review.authorName }}
                    </h3>
                    <time :datetime="review.createdAt" class="text-sm text-muted-foreground">
                      {{ d(review.createdAt) }}
                    </time>
                  </div>

                  <div class="mt-1 flex items-center">
                    <Rating :rating="review.rating" class="-ml-0.5" />
                    <p class="sr-only">
                      {{ t('product.rating.x_out_of_y_stars.label', { x: review.rating, y: 5 }) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Purchase info -->
              <div class="mb-4 self-start text-sm text-muted-foreground">
                {{ t('review.purchased') }} {{ purchasedProductOptions }}
              </div>

              <!-- Review content -->
              <div class="mb-6 w-full self-start space-y-3">
                <div v-if="review.summary" class="text-foreground font-medium">
                  {{ review.summary }}
                </div>

                <div v-if="review.body" class="text-muted-foreground">
                  {{ review.body }}
                </div>
              </div>

              <!-- Asset thumbnails -->
              <div v-if="review.assets?.length > 1" class="mb-6 w-full self-start">
                <h4 class="mb-2 text-sm font-medium">
                  {{ t('review.photos_in_this_review') }}
                </h4>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(asset, index) in review.assets"
                    :key="asset.preview"
                    class="relative h-16 w-16 cursor-pointer overflow-hidden border-2 rounded-md transition-all"
                    :class="index === currentAssetIndex ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'"
                    @click="currentAssetIndex = index"
                  >
                    <!-- Video indicator -->
                    <UiButton
                      v-if="isVideo(asset.type)"
                      variant="outline"
                      size="icon"
                      class="absolute left-1/2 top-1/2 z-10 transform rounded-full -translate-x-1/2 -translate-y-1/2"
                    >
                      <Icon name="lucide:play" class="size-4 text-muted-foreground" />
                    </UiButton>

                    <video
                      v-if="isVideo(asset.type)"
                      :poster="asset.preview"
                      class="h-full w-full object-cover"
                      preload="metadata"
                    >
                      <source :src="asset.source" :type="asset.mimeType">
                    </video>
                    <NuxtImg
                      v-else
                      :src="asset.preview"
                      class="h-full w-full object-cover"
                      placeholder
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="mt-auto flex items-center justify-between">
              <UiButton
                variant="outline"
                size="sm"
                class="h-8"
                @click="handleUpvote(review)"
              >
                <Icon
                  :name="productReviewStore.hasVoted(review.id) ? 'i-heroicons:hand-thumb-up-16-solid' : 'i-heroicons:hand-thumb-up'"
                  class="mr-1.5 size-4"
                />
                {{ t('general.useful') }}
                <span v-if="review.upvotes > 0" class="ml-1 rounded-full bg-secondary/20 px-1.5 py-0.5 text-xs font-medium">
                  {{ review.upvotes }}
                </span>
              </UiButton>
            </div>
          </div>
        </div>
      </UiDialogContent>
    </UiDialog>
  </div>
</template>
